import { useEffect, useState } from "react";
import { View, Text, DropdownField, FieldLabel, SwitchField, InputField, Button } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch, useAppSelector } from "../../redux";
import { organizationSnippetActions } from "../../redux/reducers/organizationSnippet";
import { OrganizationSnippetCreateDialog } from "./OrganizationSnippetCreateDialog";

export type StartupSettingsFormData = {
  startupAudioUrl?: string;
  startupAudioInterruptible?: boolean;
  startupAudioTranscription?: string;
  startupConnectionTimeoutSeconds?: number;
  recordingEnabled?: boolean;
  recordingLocation?: "local" | null;
};

type StartupSettingsFormProps = {
  config: StartupSettingsFormData;
  onChange: (key: string) => (value: unknown) => void;
  organizationId: string;
};

export const StartupSettingsForm = ({ config, onChange, organizationId }: StartupSettingsFormProps) => {
  const dispatch = useAppDispatch();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const organizationSnippets = useAppSelector((state) => state.organizationSnippets.entities);

  const audioSnippets = Object.values(organizationSnippets).filter((snippet) => snippet.type === "audio");

  useEffect(() => {
    if (organizationId) {
      dispatch(organizationSnippetActions.get({ organizationId }));
    }
  }, [organizationId, dispatch]);

  const onChangeRecordingEnabled = (value: string | null) => {
    const newRecordingEnabled = value === "true";

    // Update the form state
    onChange("recordingEnabled")(newRecordingEnabled);
  };

  const onClickRemoveStartupAudioUrl = () => {
    onChange("")("");

    // If recording is enabled, disable it since it requires a startup audio snippet
    if (config.recordingEnabled) {
      onChange("recordingEnabled")(false);
    }
  };

  return (
    <View className="space-y-6">
      <InputField
        label="Startup Connection Timeout"
        labelClassName="!w-full"
        className="!w-full"
        tooltip="The timeout in seconds to allow ringing before the call is ended. Default: 60 seconds."
        value={config.startupConnectionTimeoutSeconds}
        onChange={(value) => onChange("startupConnectionTimeoutSeconds")(value === "" || value === null || value === undefined ? null : Number(value))}
        type="number"
      />
      <FieldLabel
        label="Startup Audio Snippet"
        tooltip="Select an audio snippet to be played at the beginning of the session before your handler outputs audio."
      >
        <View className="flex flex-col w-full gap-2">
          <View className="flex items-center w-full gap-2">
            <DropdownField
              name="startupAudioUrl"
              placeholder="Select an audio snippet"
              value={config.startupAudioUrl || ""}
              onChange={onChange("startupAudioUrl")}
              options={audioSnippets.map((snippet) => ({
                label: snippet.name,
                value: snippet.data,
              }))}
              className="w-full"
            />

            <Button
              variant="success"
              size="auto"
              onClick={() => setShowCreateDialog(true)}
              className="whitespace-nowrap"
            >
              <Icon
                name="add"
                size={14}
              />
              Create Snippet
            </Button>

            {config.startupAudioUrl && (
              <button
                type="button"
                onClick={onClickRemoveStartupAudioUrl}
                title="Remove startup audio"
                className="flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                <Icon
                  name="close"
                  size={16}
                />
              </button>
            )}
          </View>

          {config.startupAudioUrl && (
            <audio
              src={config.startupAudioUrl}
              controls
              className="w-full"
            />
          )}
        </View>
      </FieldLabel>

      <SwitchField
        label="Startup Audio Interruptible"
        labelClassName="!w-full"
        className="!w-full"
        tooltip="Allow the startup audio to be interrupted when your handler first outputs audio."
        value={config.startupAudioInterruptible ? "true" : "false"}
        onChange={(value) => onChange("startupAudioInterruptible")(value === "true")}
        options={[
          { label: "Not Interruptible", value: "false" },
          { label: "Interruptible", value: "true" },
        ]}
      />

      <InputField
        label="Startup Audio Transcription"
        name="Startup Audio Transcription"
        placeholder="Enter the text you want included in the session transcript..."
        tooltip="This text will be included in the session transcription if startup audio is supplied."
        type="textarea"
        value={config.startupAudioTranscription}
        onChange={(value) => onChange("startupAudioTranscription")(value || null)}
        className="w-full"
      />

      <SwitchField
        label="Global Session Recording"
        labelClassName="!w-full"
        className="!w-full"
        tooltip="Enable recording at the start of the session."
        value={config.recordingEnabled ? "true" : "false"}
        onChange={onChangeRecordingEnabled}
        options={[
          { label: "Disabled", value: "false" },
          { label: "Enabled", value: "true" },
        ]}
      />

      {config.recordingEnabled && (
        <SwitchField
          label="Session Recording Storage"
          labelClassName="!w-full"
          className="!w-full"
          tooltip="Choose where to store session recordings. VoiceRun can record and store or this can be handled by your telephony provider."
          value={config.recordingLocation ?? null}
          onChange={(value) => onChange("recordingLocation")(value)}
          options={[
            { label: "VoiceRun", value: "local" },
            { label: "Telephony Provider", value: null },
          ]}
        />
      )}

      <OrganizationSnippetCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        organizationId={organizationId}
        onCreated={(snippet) => {
          onChange("startupAudioUrl")(snippet.data);
          setShowCreateDialog(false);
        }}
      />

      {!config.startupAudioUrl && config.recordingEnabled && (
        <View className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <View className="flex items-start gap-2">
            <Icon
              name="info"
              size={16}
              className="text-red-500 mt-0.5"
            />
            <Text className="text-sm text-red-700 dark:text-red-400">
              We strongly recommend playing a startup audio snippet at the beginning of the call informing the caller they are being recorded. It is up to you
              to determine if this is required by your organization or regulatory requirements.
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};
